<template>
  <div id="technicianMap" />
</template>

<script>
import { Circle as CircleStyle, Fill } from 'ol/style';
import { fromLonLat, transform } from 'ol/proj';
import getStatus from '@/components/selects/technician/technicianStatus';
import { generateMessage } from '@/utils/i18n';
import GeoJSON from 'ol/format/GeoJSON';
import Icon from 'ol/style/Icon';
import Map from 'ol/Map';
import OSM from 'ol/source/OSM';
import Stroke from 'ol/style/Stroke';
import StyleLayer from 'ol/style/Style';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import View from 'ol/View';

import 'ol/ol.css';
import centerMarker from '@/assets/map-icons/map-marker-32.png';
import mntRecourseTechMarker from '@/assets/map-icons/account-star-outline.png';
import mntResponsibleTechMarker from '@/assets/map-icons/account-hard-hat-outline.png';
import mntResponsibleTechBuddyMarker from '@/assets/map-icons/account-convert-outline.png';
import onCallTechMarker from '@/assets/map-icons/account-cowboy-hat-outline.png';
import techInChargeMarker from '@/assets/map-icons/account-cog-outline.png';
import technicianMarker from '@/assets/map-icons/account-outline.png';

const MAP_TRANSITION_DURATION = 2000;
const DEFAULT_ZOOM = 14;
const DEG_TO_RAD = (2 * Math.PI) / 360;
const EARTH_RADIUS = 6378.137;

export default {
  name: 'TechnicianSelectorMap',
  components: {},
  props: {
    displayedTechId: { type: Number, required: false, default: -1 },
    refresh: { type: Boolean, required: false },
    request: { type: Object, required: true },
    specialTechnicians: { type: Object, required: true },
    technicians: { type: Array, required: true },
  },
  computed: {
    centerCoordinates() {
      return [this.property.longitude, this.property.latitude];
    },
    mapCenter() {
      return {
        id: 0,
        intervention_longitude: this.property.longitude,
        intervention_latitude: this.property.latitude,
      };
    },
    property() {
      return this.request.property ? this.request.property : this.request;
    },
  },
  data() {
    return {
      distances: [],
      mapInstance: null,
      layers: [],
      specialDistances: [],
    };
  },
  mounted() {
    this.renderMap(true);
  },
  watch: {
    centerCoordinates() {
      this.renderMap();
    },
    displayedTechId(newVal, oldVal) {
      if (
        [0, ...this.technicians.map((item) => item.id)].includes(newVal) ||
        [0, ...this.technicians.map((item) => item.id)].includes(oldVal)
      )
        this.renderMap();
    },
    refresh() {
      this.mapInstance.setView(this.getView(this.centerCoordinates));
    },
    specialTechnicians() {
      this.renderMap();
    },
    technicians() {
      this.renderMap();
    },
  },
  methods: {
    generateMessage,
    getIconSrc(item) {
      switch (item.id) {
        case 0:
          return centerMarker;
        case this.specialTechnicians.techInCharge.id:
          return techInChargeMarker;
        case this.specialTechnicians.mntRecourseTech.id:
          return mntRecourseTechMarker;
        case this.specialTechnicians.mntResponsibleTech.id:
          return mntResponsibleTechMarker;
        case this.specialTechnicians.mntResponsibleTechBuddy.id:
          return mntResponsibleTechBuddyMarker;
        case this.specialTechnicians.onCallTechs.find(
          (tech) => tech.id === item.id
        ):
          return onCallTechMarker;
        default:
          return technicianMarker;
      }
    },
    getStyle(item, isHighLight) {
      if (isHighLight) {
        return new StyleLayer({
          image: new CircleStyle({
            displacement: [0, 11],
            radius: 13,
            fill: new Fill({ color: '#fff' }),
            stroke: new Stroke({ color: '#000' }),
          }),
        });
      }
      return new StyleLayer({
        image: new Icon({
          anchor: [0.5, 1],
          color: getStatus(item).color,
          crossOrigin: 'anonymous',
          scale: 0.7,
          src: this.getIconSrc(item),
        }),
      });
    },
    getPoint(longitude, latitude) {
      const point = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [longitude, latitude],
        },
      };
      return new GeoJSON().readFeature(point, {
        featureProjection: 'EPSG:3857',
      });
    },
    getVectorLayer(item, isHighLight) {
      return new VectorLayer({
        source: new VectorSource({
          features: [
            this.getPoint(
              item.intervention_longitude,
              item.intervention_latitude
            ),
          ],
        }),
        style: this.getStyle(item, isHighLight),
      });
    },
    setTechnician(item) {
      if (!item || !(item.intervention_longitude && item.intervention_latitude))
        return;
      this.layers.push(this.getVectorLayer(item));
      this.setDistance(item);
    },
    getEuclidianDistance(coord1, coord2) {
      const longitude1 = coord1[0] * DEG_TO_RAD;
      const latitude1 = coord1[1] * DEG_TO_RAD;
      const longitude2 = coord2[0] * DEG_TO_RAD;
      const latitude2 = coord2[1] * DEG_TO_RAD;

      return (
        EARTH_RADIUS *
        Math.acos(
          Math.sin(latitude1) * Math.sin(latitude2) +
            Math.cos(latitude1) *
              Math.cos(latitude2) *
              Math.cos(longitude2 - longitude1)
        )
      );
    },
    getTchebychevDistance(coord1, coord2) {
      const longitude = Math.abs(coord1[0] - coord2[0]);
      const latitude = Math.abs(coord1[1] - coord2[1]);
      return EARTH_RADIUS * Math.max(longitude, latitude) * DEG_TO_RAD;
    },
    setDistance(item) {
      const kilometers = this.getTchebychevDistance(
        [item.intervention_longitude, item.intervention_latitude],
        this.centerCoordinates
      );
      if (
        Object.keys(this.specialTechnicians)
          .map((specialTech) => this.specialTechnicians[specialTech].id)
          .includes(item.id)
      )
        this.specialDistances.push(kilometers);
      else this.distances.push(kilometers);
    },
    getZoom() {
      if (this.distances.length === 0 && this.specialDistances.length === 0)
        return DEFAULT_ZOOM;

      let distanceMin = 0;
      if (this.specialDistances.length > 0) {
        this.specialDistances.sort((a, b) => a - b);
        distanceMin = this.specialDistances.at(
          this.specialDistances.length < 5
            ? this.specialDistances.length - 1
            : 4
        );
      }
      if (this.specialDistances.length < 5 && this.distances.length > 0) {
        this.distances.sort((a, b) => a - b);
        distanceMin = Math.max(
          distanceMin,
          this.distances.at(
            this.distances.length > 5 - this.specialDistances.length
              ? 5 - this.specialDistances.length - 1
              : this.specialDistances.length - 1
          )
        );
      }
      return distanceMin !== 0
        ? DEFAULT_ZOOM - Math.log2(Math.ceil(distanceMin))
        : DEFAULT_ZOOM;
    },
    getView(coordinates) {
      return new View({
        center: fromLonLat([coordinates[0], coordinates[1]]),
        zoom: this.getZoom(),
      });
    },
    async renderMap(isNewMap) {
      this.distances = [];
      this.specialDistances = [];
      this.layers = [new TileLayer({ source: new OSM() })];
      const displayed = [this.mapCenter, ...this.technicians].find(
        (item) => item.id === this.displayedTechId
      );
      if (displayed) {
        this.layers.push(this.getVectorLayer(displayed, true));
      }
      this.layers.push(this.getVectorLayer(this.mapCenter));
      this.technicians.forEach((item) => this.setTechnician(item));

      if (isNewMap) {
        this.mapInstance = new Map({
          target: 'technicianMap',
          layers: this.layers,
          view: this.getView(this.centerCoordinates),
        });
      } else {
        this.mapInstance.setLayers(this.layers);
        if (displayed)
          /* Kept as comment. Maybe we can do something with this animation one day
          this.flyTo(
            this.mapInstance.getView(),
            fromLonLat([
              displayed.intervention_longitude,
              displayed.intervention_latitude,
            ])
          );
          */
          this.mapInstance.setView(
            this.getView([
              displayed.intervention_longitude,
              displayed.intervention_latitude,
            ])
          );
        else this.mapInstance.setView(this.getView(this.centerCoordinates));
      }
      this.mapInstance.on('pointermove', this.setPointerCursor);
      this.mapInstance.on('click', this.displayInfos);
    },
    displayInfos(event) {
      if (this.mapInstance.hasFeatureAtPixel(event.pixel)) {
        const technician = this.getTechnician(
          this.getFeatureCoordinates(event)
        );
        if (technician) {
          this.$emit('click', technician);
          this.highlightElement(technician.id);
        } else {
          this.$emit('click', this.mapCenter);
          this.highlightElement(this.mapCenter.id);
        }
      } else {
        this.$emit('click', { id: -1 });
      }
    },
    getFeatureCoordinates(event) {
      const feature = this.mapInstance.getFeaturesAtPixel(event.pixel)[0];
      const featureCoords = transform(
        feature.getGeometry().getClosestPoint(event.coordinate),
        'EPSG:3857',
        'EPSG:4326'
      );
      featureCoords[0] = parseFloat(featureCoords[0].toFixed(8));
      featureCoords[1] = parseFloat(featureCoords[1].toFixed(8));
      return featureCoords;
    },
    getTechnician(coordinates) {
      return this.technicians.find(
        (item) =>
          JSON.stringify([
            item.intervention_longitude,
            item.intervention_latitude,
          ]) === JSON.stringify(coordinates)
      );
    },
    setPointerCursor(event) {
      if (this.mapInstance.hasFeatureAtPixel(event.pixel)) {
        this.mapInstance.getViewport().style.cursor = 'pointer';
      } else {
        this.mapInstance.getViewport().style.cursor = 'inherit';
      }
    },
    highlightElement(id) {
      if (id >= 0) {
        const element = document.getElementById(`techCard-${id}`);
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    flyTo(view, location) {
      const zoom = view.getZoom();
      view.animate(
        {
          duration: MAP_TRANSITION_DURATION / 2,
          zoom: zoom - 1,
        },
        {
          center: location,
          duration: MAP_TRANSITION_DURATION / 2,
          zoom,
        }
      );
    },
  },
};
</script>
